import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Fir5471ScheduleEService } from './fir-5471-schedule-e.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir-5471-schedule-e',
    templateUrl: './fir-5471-schedule-e.component.html',
    styleUrls: ['./fir-5471-schedule-e.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Fir5471ScheduleEComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subscriptions = new Subscription();
  private readonly destroyed$ = new Subject<void>();
  private isInitialized = false;

  minimise = false;
  crudLoading = false;
  showFormLoader = false;
  modalTitle = 'Schedule E (5471)';

  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private service: Fir5471ScheduleEService,
    private gtwSnackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetTryInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetTryInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const rowObj = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.rowData = rowObj;
    this.cdr.markForCheck();
  }

  private resolveApiBase(): string {
    return (
      this.GENERAL_CONFIG?.base_url ||
      this.baseURLs?.api ||
      this.baseURLs?.base_url ||
      ''
    ).toString();
  }

  private resetTryInit(): void {
    this.isInitialized = false;
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    const baseOk = !!this.resolveApiBase();
    if (!baseOk || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.cdr.detectChanges();
  }

  save(): void {
    const baseUrl = this.resolveApiBase();
    if (this.crudLoading || !baseUrl) return;
    this.crudLoading = true;
    this.cdr.markForCheck();
    const gp = extractObject(this.globalService?.globalParams);
    const jsonSettings = [
      {
        tax_year: this.filterParams?.tax_year ?? gp.tax_year,
        scenario: this.filterParams?.scenario ?? gp.scenario,
        jcd_key: (gp.jcd_key === '' || gp.jcd_key == null) ? '250' : gp.jcd_key
      }
    ];
    const jsonObj = { addrows: [], editrows: [], removerows: [] };
    this.service
      .saveScheduleE(
        baseUrl,
        { jsonSettings, jsonObj },
        this.globalService,
        this.filterParams,
        this.rowData
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (r: any) => {
          this.crudLoading = false;
          if (r?.callSuccess === '1') {
            this.gtwSnackbar.openSnackBar('Data saved successfully.', 'gtw-snackbar--green', true, 4);
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbar.openSnackBar(r?.errorMessage || 'Save failed.', 'gtw-snackbar--red', true, 4);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.crudLoading = false;
          this.gtwSnackbar.openSnackBar('Error saving. Please try again.', 'gtw-snackbar--red', true, 4);
          this.cdr.markForCheck();
        }
      });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
